<script lang="ts">
	import { enumToJson, toJson } from "@bufbuild/protobuf";
	import { DragDropProvider, KeyboardSensor, PointerSensor } from "@dnd-kit/svelte";
	import { isSortable } from "@dnd-kit/svelte/sortable";
	import deepEqual from "deep-equal";
	import { A, Dropdown, DropdownItem } from "flowbite-svelte";
	import { ContactDataSchema, SocialLinkSchema, SocialLinkTypeSchema, type ContactData, type ContactDataJson, type SocialLink, type SocialLinkType } from "#gen/bundles/v1/contact_pb.js";
	import Icon from "#lib/components/Icon.svelte";
	import P from "#lib/components/P.svelte";
	import TogglePane from "#lib/components/TogglePane.svelte";
	import SocialLinksContainer from "#lib/components/flow/editor/content/contact/SocialLinksContainer.svelte";
	import type { DragEndEvent, DragOverEvent } from "#lib/components/flow/editor/types.js";
	import { socialIcons } from "#lib/iconProvider.js";
	import { getSocialName, validateSocialLinkId } from "#lib/social.js";
	import { ERR_REQUIRED } from "#lib/validation.js";

	interface Props {
		data: ContactData;
		onUpdate?: (data: ContactDataJson) => void;
	}

	const socialLinkTypes = [...socialIcons.entries()].sort((a, b) => getSocialName(a[0]).localeCompare(getSocialName(b[0])));

	let { data, onUpdate }: Props = $props();
	let addMenuOpen = $state(false);
	let errors: unknown[] = $state([]);

	let socialLinks = $derived(data.socialLinks);
	let snapshot: SocialLink[] = [];

	export async function validate(): Promise<boolean> {
		data.socialLinks?.forEach((link, i) => validateSocialLink(link, i));
		return errors.every(e => e === undefined);
	}

	function handleAddSocialLink(e: Event, type: SocialLinkType) {
		e.preventDefault();
		const update = toJson(ContactDataSchema, data) as ContactDataJson;
		if (!update.socialLinks) {
			update.socialLinks = [];
		}
		update.socialLinks.push({ type: enumToJson(SocialLinkTypeSchema, type) });
		onUpdate?.(update);
		addMenuOpen = false;
	}

	function onRemove(_link: SocialLink, i: number) {
		const update = toJson(ContactDataSchema, data) as ContactDataJson;
		if (!update.socialLinks) {
			return;
		}
		update.socialLinks.splice(i, 1);
		errors.splice(i, 1);
		onUpdate?.(update);
	}

	function onChange(link: SocialLink, i: number) {
		if (validateSocialLink(link, i) === false) {
			return;
		}
		const update = toJson(ContactDataSchema, data) as ContactDataJson;
		update.socialLinks = update.socialLinks || [];
		update.socialLinks[i] = toJson(SocialLinkSchema, link);
		onUpdate?.(update);
	}

	function onDragStart() {
		snapshot = data.socialLinks.slice();
	}

	function onDragOver(event: DragOverEvent) {
		const { source, target } = event.operation;
		if (isSortable(source) && isSortable(target)) {
			const fromIndex = source.index;
			const toIndex = target.index;

			if (fromIndex !== toIndex) {
				const newSocialLinks = [...socialLinks];
				const [removed] = newSocialLinks.splice(fromIndex, 1);
				newSocialLinks.splice(toIndex, 0, removed);
				socialLinks = newSocialLinks;
			}
		}
	}

	function onDragEnd(event: DragEndEvent) {
		if (event.canceled || deepEqual(socialLinks, snapshot)) {
			socialLinks = snapshot;
			return;
		}
		const update = toJson(ContactDataSchema, data) as ContactDataJson;
		update.socialLinks = socialLinks.map(link => toJson(SocialLinkSchema, link));
		onUpdate?.(update);
	}

	function validateSocialLink(link: SocialLink, i: number): boolean {
		const id = link.id;
		if (!id) {
			setError(i, ERR_REQUIRED);
			return false;
		}
		if (!validateSocialLinkId(link)) {
			setError(i, "Invalid URL. Please check the format and try again.");
			return false;
		}
		clearError(i);
		return true;
	}

	function setError(i: number, error: string) {
		errors[i] = error;
		errors = errors;
	}

	function clearError(i: number) {
		errors[i] = undefined;
		errors = errors;
	}
</script>

{#snippet buttons()}
	{#if (data.socialLinks?.length || 0) < 5}
		<div>
			<A class="text-sm" data-action-name="contact-details-add-social-link">Add social link</A>
			<Dropdown bind:open={addMenuOpen}>
				{#each socialLinkTypes as [type, icon] (type)}
					{#if (data.socialLinks?.findIndex(link => link.type === type) || -1) === -1}
						<DropdownItem on:click={e => handleAddSocialLink(e, type)}>
							<Icon iconSet={icon?.iconSet} name={icon?.icon} class="mr-2 inline-block" />
							{getSocialName(type)}
						</DropdownItem>
					{/if}
				{/each}
			</Dropdown>
		</div>
	{:else}
		<P size="sm" class="text-gray-500" secondary>You can add up to 5 social media links.</P>
	{/if}
{/snippet}

<TogglePane enabled={true} label="Social information">
	{#snippet control()}<div></div>{/snippet}
	<DragDropProvider sensors={[KeyboardSensor, PointerSensor]} {onDragStart} {onDragOver} {onDragEnd}>
		<SocialLinksContainer id="social-links-dropzone" links={socialLinks} {errors} onInput={validateSocialLink} {onChange} {onRemove}>
			{#snippet footer()}
				{@render buttons()}
			{/snippet}
		</SocialLinksContainer>
	</DragDropProvider>
</TogglePane>
